// Pure helpers for the full-screen map (no DOM, so tests can import them): color mixing and label placement.

export interface Box { x: number; y: number; w: number; h: number }

/** Mix two #rrggbb colors; t = 0 gives a, t = 1 gives b. Returns #rrggbb. */
export function mixHex(a: string, b: string, t: number): string {
  const p = (h: string, i: number) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  const ch = (i: number) => Math.round(p(a, i) + (p(b, i) - p(a, i)) * Math.max(0, Math.min(1, t))).toString(16).padStart(2, '0');
  return `#${ch(0)}${ch(1)}${ch(2)}`;
}

export const overlaps = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** Place a w x h label near the anchor point: right, left, above, below, then the diagonals. Takes the first spot that
 *  stays inside `bounds` and clear of `taken`; falls back to the first spot that stays inside, else the right-hand spot. */
export function placeLabel(anchor: { x: number; y: number }, w: number, h: number, gap: number, taken: Box[], bounds: Box): Box {
  const spots: Box[] = [
    { x: anchor.x + gap, y: anchor.y - h / 2, w, h },
    { x: anchor.x - gap - w, y: anchor.y - h / 2, w, h },
    { x: anchor.x - w / 2, y: anchor.y - gap - h, w, h },
    { x: anchor.x - w / 2, y: anchor.y + gap, w, h },
    { x: anchor.x + gap, y: anchor.y - gap - h, w, h },
    { x: anchor.x - gap - w, y: anchor.y - gap - h, w, h },
    { x: anchor.x + gap, y: anchor.y + gap, w, h },
    { x: anchor.x - gap - w, y: anchor.y + gap, w, h },
  ];
  const inside = (b: Box) => b.x >= bounds.x && b.y >= bounds.y && b.x + b.w <= bounds.x + bounds.w && b.y + b.h <= bounds.y + bounds.h;
  return spots.find(b => inside(b) && !taken.some(t => overlaps(b, t))) ?? spots.find(inside) ?? spots[0];
}
